import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Upload, MapPinned, Trash2, Pencil, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import MapAnnotator from "@/components/MapAnnotator";
import MapPipelinePanel from "@/components/MapPipelinePanel";
import useAuthenticatedObjectUrl from "../lib/useAuthenticatedObjectUrl";
import { useAppAuth } from "@/auth/AppAuthProvider";

const emptyT = { name: "", location_name: "", lat: 0, lng: 0, timezone: "UTC", operating_hours: "24/7", contact: "", vertical_datum: "MSL" };
const emptyB = { terminal_id: "", name: "", length_m: 300, depth_m: 10, max_loa_m: 300, min_ukc_m: 0.8, dock_elevation_m: 0, ship_side_to_bullrail_m: 0, vertical_datum: "MSL", status: "open", x1: 100, y1: 200, x2: 700, y2: 260, scale_m_per_px: 0.667, orientation_deg: 0 };

export default function Terminals() {
  const { hasCapability } = useAppAuth();
  const canManageReference = hasCapability("reference.manage");
  const canManageMap = hasCapability("map.manage");
  const [terms, setTerms] = useState([]);
  const [berths, setBerths] = useState([]);
  const [maps, setMaps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openT, setOpenT] = useState(false);
  const [openB, setOpenB] = useState(false);
  const [editingBerthId, setEditingBerthId] = useState(null);
  const [editingBerthRevision, setEditingBerthRevision] = useState(null);
  const [tForm, setTForm] = useState(emptyT);
  const [bForm, setBForm] = useState(emptyB);
  const [reviewMapId, setReviewMapId] = useState(null);
  const [openMapSettings, setOpenMapSettings] = useState(false);
  const [openConditionSettings, setOpenConditionSettings] = useState(false);
  const [mapForm, setMapForm] = useState({ west: "", south: "", east: "", north: "", zoom: 19, status: "preliminary" });
  const [conditionForm, setConditionForm] = useState({ vertical_datum: "MSL" });

  const refresh = useCallback(async () => {
    const t = await api.list("terminals");
    setTerms(t);
    const activeTerminalId = t.some(item => item.id === selected) ? selected : (t[0]?.id || null);
    if (activeTerminalId !== selected) setSelected(activeTerminalId);
    const all = await Promise.all(t.map(x => api.list("berths", { terminal_id: x.id })));
    setBerths(all.flat());
    if (activeTerminalId) {
      const m = await api.list("maps", { terminal_id: activeTerminalId });
      setMaps(m);
    } else setMaps([]);
  }, [selected]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    setReviewMapId(null);
  }, [selected]);

  const createTerminal = async () => {
    if (!tForm.name) return toast.error("Name required");
    await api.create("terminals", tForm);
    setOpenT(false); setTForm(emptyT);
    toast.success("Terminal created");
    refresh();
  };
  const createBerth = async () => {
    if (!bForm.name || !selected) return toast.error("Name + terminal required");
    if (editingBerthId) await api.patch("berths", editingBerthId, { ...bForm, terminal_id: selected }, editingBerthRevision);
    else await api.create("berths", { ...bForm, terminal_id: selected });
    setOpenB(false); setBForm(emptyB);
    toast.success(editingBerthId ? "Berth updated" : "Berth created");
    setEditingBerthId(null);
    refresh();
  };
  const editBerth = (berth) => {
    setEditingBerthId(berth.id);
    setEditingBerthRevision(berth.revision);
    setBForm(Object.fromEntries(Object.keys(emptyB).map(key => [key, berth[key] ?? emptyB[key]])));
    setOpenB(true);
  };

  const editMapSettings = () => {
    const terminal = terms.find(item => item.id === selected);
    const bbox = terminal?.map_bbox || [];
    setMapForm({ west: bbox[0] ?? "", south: bbox[1] ?? "", east: bbox[2] ?? "", north: bbox[3] ?? "", zoom: terminal?.map_default_zoom ?? 19, status: terminal?.map_georef_status || "preliminary" });
    setOpenMapSettings(true);
  };

  const saveMapSettings = async () => {
    const terminal = terms.find(item => item.id === selected);
    if (!terminal) return;
    const bbox = [mapForm.west, mapForm.south, mapForm.east, mapForm.north].map(Number);
    if (bbox.some(value => !Number.isFinite(value)) || bbox[0] >= bbox[2] || bbox[1] >= bbox[3]) return toast.error("Enter a valid west, south, east, north BBOX");
    try {
      const result = await api.saveMapConfig(terminal.id, {
        bbox,
        default_zoom: Number(mapForm.zoom),
        basemap_provider: "esri",
        basemap_style: "world-imagery",
        spatial_reference: "EPSG:4326",
        georef_status: mapForm.status,
      }, terminal.revision);
      const updated = result.terminal;
      setTerms(current => current.map(item => item.id === updated.id ? updated : item));
      setOpenMapSettings(false);
      toast.success(`Optional Esri reference extent saved · ${result.updated_annotations || 0} annotation(s) reprojected`);
    } catch (err) { toast.error(err.response?.data?.detail || "Map settings save failed"); }
  };

  const editConditionSettings = () => {
    const terminal = terms.find(item => item.id === selected);
    if (!terminal) return;
    setConditionForm({
      vertical_datum: terminal.vertical_datum || "MSL",
    });
    setOpenConditionSettings(true);
  };

  const saveConditionSettings = async () => {
    const terminal = terms.find(item => item.id === selected);
    if (!terminal) return;
    try {
      const updated = await api.patch("terminals", terminal.id, conditionForm, terminal.revision);
      setTerms(current => current.map(item => item.id === updated.id ? updated : item));
      setOpenConditionSettings(false);
      toast.success("Environmental source settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Environmental settings save failed");
    }
  };

  const uploadMap = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selected) return;
    try {
      const m = await api.uploadMap(f, selected, 0.667);
      toast.success("Map uploaded");
      // GeoTIFF: try to auto-read embedded georeference (sets bbox_geo so OSM import is one-click)
      const isTiff = m?.content_type === "image/tiff" || /\.(tiff?)$/i.test(f.name);
      if (isTiff && m?.id) {
        try {
          const g = await api.readGeoref(m.id);
          if (g?.bbox) toast.success(`GeoTIFF georeferenced: bbox ${g.bbox.map(v => v.toFixed(3)).join(", ")}`);
        } catch (_) {
          toast.message("GeoTIFF: no embedded georeference tags found", { description: "You can still draw a boundary manually." });
        }
      }
      api.list("maps", { terminal_id: selected }).then(setMaps);
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const removeBerth = async (id) => {
    await api.remove("berths", id);
    refresh();
  };

  const selectTerminal = (terminalId) => {
    setMaps([]);
    setSelected(terminalId);
  };

  const selectedTerminal = terms.find(item => item.id === selected);
  const sBerths = berths.filter(b => b.terminal_id === selected);
  const activeMap = maps.find(m => m.is_active);
  const displayMap = maps.find(m => m.id === reviewMapId) || activeMap;
  const { url: displayMapUrl } = useAuthenticatedObjectUrl(
    displayMap ? () => api.mapFileBlob(displayMap.id) : null,
    [displayMap?.id],
  );
  const refreshMaps = async (mapId = null) => {
    const updated = await api.list("maps", { terminal_id: selected });
    setMaps(updated);
    setReviewMapId(mapId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Configuration</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Terminals &amp; Berths</h1>
        </div>
        <Dialog open={openT} onOpenChange={setOpenT}>
          {canManageReference && <DialogTrigger asChild>
            <Button data-testid={TESTIDS.btnCreateTerminal} className="bg-sky-600 hover:bg-sky-500 rounded-none">
              <Plus size={14} className="mr-1.5" /> New Terminal
            </Button>
          </DialogTrigger>}
          <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">New terminal</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Name"><Input data-testid="input-terminal-name" value={tForm.name} onChange={e => setTForm({ ...tForm, name: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
              <Field label="Location"><Input value={tForm.location_name} onChange={e => setTForm({ ...tForm, location_name: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
              <Field label="Latitude"><Input type="number" step="0.0001" value={tForm.lat} onChange={e => setTForm({ ...tForm, lat: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <Field label="Longitude"><Input type="number" step="0.0001" value={tForm.lng} onChange={e => setTForm({ ...tForm, lng: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <Field label="Timezone"><Input value={tForm.timezone} onChange={e => setTForm({ ...tForm, timezone: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
              <Field label="Hours"><Input value={tForm.operating_hours} onChange={e => setTForm({ ...tForm, operating_hours: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
              <Field label="Contact" className="col-span-2"><Input value={tForm.contact} onChange={e => setTForm({ ...tForm, contact: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
              <Field label="Vertical datum"><Input value={tForm.vertical_datum} onChange={e => setTForm({ ...tForm, vertical_datum: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
            </div>
            <DialogFooter><Button onClick={createTerminal} data-testid="btn-save-terminal" className="bg-sky-600 hover:bg-sky-500 rounded-none">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="panel p-3 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="w-full sm:max-w-md">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Terminal</Label>
          <Select value={selected || undefined} onValueChange={selectTerminal} disabled={!terms.length}>
            <SelectTrigger data-testid={TESTIDS.terminalSelector} className="mt-1 bg-[#050B14] border-[#1E293B] rounded-none h-10">
              <SelectValue placeholder={terms.length ? "Select terminal..." : "No terminals configured"} />
            </SelectTrigger>
            <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
              {terms.map(terminal => <SelectItem key={terminal.id} value={terminal.id}>{terminal.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {selectedTerminal && <div className="pb-1 font-mono text-[10px] text-slate-500">{selectedTerminal.location_name} · {selectedTerminal.timezone}</div>}
      </div>

      {selected ? (
        <div className="space-y-4">
          <div className="panel p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Source drawing package</div>
                    <div className="font-heading text-sm">
                      {displayMap ? displayMap.original_filename : "No map uploaded"}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Reference source for extracting engineering geometry; it is not the collision surface.</div>
                  </div>
                  <div className="flex items-center gap-2">
                  {canManageReference && <Button size="sm" variant="secondary" onClick={editConditionSettings} className="bg-[#162032] border border-[#1E293B] rounded-none text-slate-200" data-testid="btn-condition-settings"><Settings size={13} className="mr-1.5" />Tide datum</Button>}
                  {canManageMap && <Button size="sm" variant="secondary" onClick={editMapSettings} className="bg-[#162032] border border-[#1E293B] rounded-none text-slate-200" data-testid="btn-map-settings"><Settings size={13} className="mr-1.5" />Esri reference</Button>}
                  {canManageMap && <label className="cursor-pointer">
                    <Input type="file" accept="image/*,application/pdf,.tif,.tiff" onChange={uploadMap} className="hidden" data-testid={TESTIDS.inputMapFile} />
                    <span data-testid={TESTIDS.btnUploadMap} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] text-slate-200 cursor-pointer">
                      <Upload size={14} /> Upload map
                    </span>
                  </label>}
                  </div>
                </div>
                {displayMap && displayMap.content_type !== "application/pdf" && displayMap.content_type !== "application/dxf" && (
                  <img
                    src={displayMapUrl || undefined}
                    alt="terminal map"
                    className="max-h-72 w-full object-contain bg-[#050B14] border border-[#1E293B]"
                    data-testid="active-map-image"
                  />
                )}
                {displayMap?.content_type === "application/pdf" && (
                  <div className="panel-2 p-4 text-xs font-mono text-slate-400" data-testid="pdf-map-link">PDF map stored. Open in the annotator below or in a new tab: <a className="text-sky-400" href={displayMapUrl || undefined} target="_blank" rel="noreferrer">{displayMap.original_filename}</a></div>
                )}
                {displayMap?.content_type === "application/dxf" && <div className="panel-2 p-4 text-xs font-mono text-slate-400">DXF source stored. Extracted geometry is displayed in the annotator below.</div>}
                <Dialog open={openMapSettings} onOpenChange={setOpenMapSettings}>
                  <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-xl">
                    <DialogHeader><DialogTitle className="font-heading">Optional Esri reference imagery</DialogTitle></DialogHeader>
                    <div className="text-xs text-slate-400">This extent displays imagery behind a copy of the operational vectors for visual reference only. Collision and positioning calculations continue to use the local metric engineering plan.</div>
                    <div className="text-xs text-slate-500">WGS84 BBOX order: west, south, east, north. “Validated” means the visual overlay was checked against control points; it does not replace engineering survey control.</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[["west", "West longitude"], ["south", "South latitude"], ["east", "East longitude"], ["north", "North latitude"]].map(([key, label]) => (
                        <Field key={key} label={label}><Input data-testid={`input-map-${key}`} type="number" step="0.000001" value={mapForm[key]} onChange={e => setMapForm({ ...mapForm, [key]: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                      ))}
                      <Field label="Default zoom"><Input data-testid="input-map-zoom" type="number" min="0" max="23" value={mapForm.zoom} onChange={e => setMapForm({ ...mapForm, zoom: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                      <Field label="Georeference status">
                        <Select value={mapForm.status} onValueChange={value => setMapForm({ ...mapForm, status: value })}>
                          <SelectTrigger data-testid="select-map-georef-status" className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none"><SelectItem value="preliminary">preliminary</SelectItem><SelectItem value="validated">validated</SelectItem></SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <DialogFooter><Button onClick={saveMapSettings} data-testid="btn-save-map-settings" className="bg-sky-600 hover:bg-sky-500 rounded-none">Save extent</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
                <Dialog open={openConditionSettings} onOpenChange={setOpenConditionSettings}>
                  <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading">Environmental source settings</DialogTitle></DialogHeader>
                    <div className="text-xs text-slate-400">
                      Stormglass is the configured source for weather, wind, visibility, waves, and hourly tide. A normal refresh uses two API requests and results are cached for six hours.
                    </div>
                    <div className="text-xs text-amber-300">
                      The tide datum must match the berth and asset configuration. A provider error, missing tide data, stale data, incomplete ETA–ETD coverage, or a datum mismatch requires manual intervention at approval.
                    </div>
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <Field label="Terminal vertical datum"><Input data-testid="input-condition-vertical-datum" value={conditionForm.vertical_datum} onChange={e => setConditionForm({ ...conditionForm, vertical_datum: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                    </div>
                    <div className="text-[10px] text-slate-500">
                      The API key and plan are deployment secrets, not terminal fields. If live data is unavailable, record verified manual conditions with their source, datum, and validity window or use the audited blocking-conflict override.
                    </div>
                    <DialogFooter><Button onClick={saveConditionSettings} data-testid="btn-save-condition-settings" className="bg-sky-600 hover:bg-sky-500 rounded-none">Save source</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Annotator (reviewer drawing tools) */}
              {canManageMap && <div className="panel p-4">
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Engineering terminal plan</div>
                  <div className="font-heading text-sm">Metric berths, chainage, operational zones, no-go areas, and access routes</div>
                </div>
                <MapAnnotator key={`${selected}-${terms.find(item => item.id === selected)?.revision || 0}`} terminalId={selected} mapFile={displayMap} berths={sBerths} terminalName={terms.find(item => item.id === selected)?.name || "Terminal"} />
              </div>}

              {canManageMap && <MapPipelinePanel
                terminalId={selected}
                onMapReady={(mapId) => refreshMaps(mapId)}
                onPublished={() => refreshMaps(null)}
              />}
              <div className="panel p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Berths</div>
                    <div className="font-heading text-sm">{sBerths.length} configured</div>
                  </div>
                  <Dialog open={openB} onOpenChange={setOpenB}>
                    {canManageReference && <DialogTrigger asChild>
                      <Button data-testid={TESTIDS.btnCreateBerth} onClick={() => { setEditingBerthId(null); setEditingBerthRevision(null); setBForm(emptyB); }} size="sm" className="bg-sky-600 hover:bg-sky-500 rounded-none">
                        <Plus size={14} className="mr-1.5" /> New Berth
                      </Button>
                    </DialogTrigger>}
                    <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-xl">
                      <DialogHeader><DialogTitle className="font-heading">{editingBerthId ? "Edit" : "New"} berth</DialogTitle></DialogHeader>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <Field label="Name"><Input data-testid="input-berth-name" value={bForm.name} onChange={e => setBForm({ ...bForm, name: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
                        <Field label="Status">
                          <Select value={bForm.status} onValueChange={v => setBForm({ ...bForm, status: v })}>
                            <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                              <SelectItem value="open">open</SelectItem><SelectItem value="closed">closed</SelectItem><SelectItem value="restricted">restricted</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        {["length_m", "depth_m", "max_loa_m", "min_ukc_m", "dock_elevation_m", "ship_side_to_bullrail_m"].map(k => (
                          <Field key={k} label={k}><Input type="number" step="0.1" value={bForm[k]} onChange={e => setBForm({ ...bForm, [k]: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                        ))}
                        {["x1", "y1", "x2", "y2", "scale_m_per_px", "orientation_deg"].map(k => (
                          <Field key={k} label={k}><Input type="number" step="0.001" value={bForm[k]} onChange={e => setBForm({ ...bForm, [k]: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                        ))}
                        <Field label="vertical_datum"><Input value={bForm.vertical_datum} onChange={e => setBForm({ ...bForm, vertical_datum: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                      </div>
                      <DialogFooter><Button onClick={createBerth} data-testid="btn-save-berth" className="bg-sky-600 hover:bg-sky-500 rounded-none">{editingBerthId ? "Save" : "Create"}</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                      <th className="text-left pb-2">Name</th><th className="text-left pb-2">LOA</th><th className="text-left pb-2">Depth</th><th className="text-left pb-2">Min UKC</th><th className="text-left pb-2">Status</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sBerths.map(b => (
                      <tr key={b.id} className="border-b border-[#1E293B] hover:bg-[#0E1522]" data-testid={`berth-row-${b.id}`}>
                        <td className="py-2"><MapPinned size={12} className="inline mr-1 text-sky-400" />{b.name}</td>
                        <td className="font-mono py-2">{b.max_loa_m}m</td>
                        <td className="font-mono py-2">{b.depth_m}m</td>
                        <td className="font-mono py-2">{b.min_ukc_m}m</td>
                        <td className="py-2"><span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${b.status === "open" ? "sev-ok" : "sev-warning"}`}>{b.status}</span></td>
                        <td className="text-right">{canManageReference && <><button title="Edit berth geometry" onClick={() => editBerth(b)} className="text-slate-500 hover:text-sky-400 mr-3"><Pencil size={12} /></button><button data-testid={`del-berth-${b.id}`} onClick={() => removeBerth(b.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button></>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
          </div>
        </div>
      ) : (
        <div className="panel p-6 text-sm text-slate-500">Create a terminal to configure its map and berths.</div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
